import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { resetProps, updateProps } from '../../contextRedux/AdministrationCompany/AdministrationCompanyModel';
import { setAdministrationCompanies } from '../../contextRedux/AdministrationCompany/AdministrationCompanyStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import AdministrationCompanyService from '../../services/administrationCompany/AdministrationCompanyService';
import { RootState } from '../../store';
import AdministrationCompanyModal from '../AdministrationCompany/AdministrationCompanyModal/AdministrationCompanyModal';
import AdministrationCompanyTable from './AdministrationCompanyTable/AdministrationCompanyTable';

const AdministrationCompany: React.FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.administrationCompany.props.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.administrationCompany.props.skip);
  const take = useSelector((state: RootState) => state.models?.administrationCompany.props.take);
  const refresh = useSelector((state: RootState) => state.models?.administrationCompany.props.refresh);

  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;

    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsParamsInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isParamsInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;

      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };

        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });

        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isParamsInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    isFetching: isAdministrationCompaniesLoading,
    isError: isErrorOnFetchAdministrationCompanies,
    error: companiesError,
    refetch,
  } = useQuery({
    queryKey: ['companies', searchTerm, skip, take, refresh],
    queryFn: () =>
      AdministrationCompanyService.search({
        termen: searchTerm || undefined,
        skip,
        take,
      }).then((data: any) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setAdministrationCompanies(data?.response));
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  const handleReset = () => {
    dispatch(
      resetProps({
        isModalVisible: true,
        refresh: true,
        searchTerm,
        skip,
        take,
      })
    );
  };

  if (isAdministrationCompaniesLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchAdministrationCompanies) {
    return <Alert message={companiesError.message} type='error' />;
  }

  return (
    <>
      <h1 style={{ marginBottom: 50 }}>Companie</h1>
      <AnimateButton onClick={() => handleReset()}>Adauga</AnimateButton>
      <AdministrationCompanyModal />
      <AdministrationCompanyTable />
    </>
  );
};

export default AdministrationCompany;
